/**
 * Détail d'un besoin de recrutement, en lecture.
 *
 * Partagé entre le répertoire des entreprises (`com-entreprises`), qui le
 * corrige, et le dossier d'une organisation (`/ped-repertoire/:id`), qui le
 * consulte : deux rendus du même besoin finiraient par se contredire, l'un
 * oubliant une rubrique que l'autre affiche.
 */
import { FileText } from 'lucide-react';
import { BoutonSecondaire, Pastille } from '../ui/Primitives';
import { urlFicheBesoin, type Besoin } from '../../lib/useEntreprises';

/** Rendu d'une date à l'heure de La Réunion (les dates arrivent en UTC). */
function jour(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

export function DetailBesoin({
  besoin,
  onEditer,
}: {
  besoin: Besoin;
  /**
   * Bouton « Modifier », posé par l'écran qui en a le droit. Absent, la fiche
   * se lit sans se corriger : le dossier d'une organisation la donne à voir,
   * la correction reste l'acte de qui prospecte (`com-entreprises`).
   */
  onEditer?: () => void;
}): JSX.Element {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Pastille texte={besoin.statutLibelle} ton={besoin.statutTon} />
        {besoin.ouvertRqth && <Pastille texte="Ouvert RQTH" ton="info" />}
      </div>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Ligne libelle="Postes" valeur={String(besoin.nombrePostes)} />
        <Ligne libelle="Formation" valeur={besoin.formation ?? 'à définir'} />
        <Ligne libelle="Type de contrat" valeur={besoin.typeContratLibelle ?? 'non précisé'} />
        <Ligne libelle="Démarrage souhaité" valeur={jour(besoin.dateSouhaitee)} />
        <Ligne libelle="Date de la demande" valeur={jour(besoin.dateDemande)} />
        <Ligne libelle="Commercial" valeur={besoin.commercial ?? 'non précisé'} />
        <Ligne libelle="Recueilli le" valeur={jour(besoin.creeLe)} />
        <Ligne
          libelle="Étude de faisabilité"
          valeur={
            besoin.faisabilite === null ? 'non tranchée' : besoin.faisabilite ? 'Oui' : 'Non'
          }
        />
      </dl>
      {besoin.motifFaisabilite && (
        <dl>
          <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Motif de la faisabilité
          </dt>
          <dd className="mt-1 whitespace-pre-line text-sm text-codialis-gray">
            {besoin.motifFaisabilite}
          </dd>
        </dl>
      )}
      <div>
        <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
          Profil recherché
        </dt>
        <dd className="mt-1 whitespace-pre-line text-sm text-codialis-gray">
          {besoin.profilRecherche ?? 'Non précisé'}
        </dd>
      </div>
      {besoin.tachesOffre.length > 0 && (
        <div>
          <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Description de l'offre
          </dt>
          <dd className="mt-1 space-y-2">
            {besoin.tachesOffre.map((ligne, index) => (
              <div
                key={index}
                className="grid grid-cols-1 gap-1 rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm text-codialis-gray sm:grid-cols-2"
              >
                <p className="whitespace-pre-line">
                  <span className="font-bold text-codialis-navy">RNCP · </span>
                  {ligne.rncp || '—'}
                </p>
                <p className="whitespace-pre-line">
                  <span className="font-bold text-codialis-navy">Apprenant · </span>
                  {ligne.taches || '—'}
                </p>
              </div>
            ))}
          </dd>
        </div>
      )}
      {(besoin.candidatNom || besoin.candidatTelephone || besoin.candidatEmail) && (
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Ligne libelle="Candidat correspondant" valeur={besoin.candidatNom ?? '—'} />
          <Ligne libelle="Téléphone du candidat" valeur={besoin.candidatTelephone ?? '—'} />
          <Ligne libelle="E-mail du candidat" valeur={besoin.candidatEmail ?? '—'} />
        </dl>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <LienFichePdf besoinId={besoin.id} />
        {onEditer && <BoutonSecondaire onClick={onEditer}>Modifier</BoutonSecondaire>}
      </div>
    </div>
  );
}

function Ligne({ libelle, valeur }: { libelle: string; valeur: string }): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
      </dt>
      <dd className="mt-0.5 text-sm text-codialis-gray">{valeur}</dd>
    </div>
  );
}

/**
 * Lien vers la fiche de recueil en PDF.
 *
 * Un lien, pas un bouton : le jeton de session voyage dans un cookie, le
 * navigateur ouvre donc l'adresse lui-même, et la fiche s'affiche dans un
 * onglet de prévisualisation d'où elle s'imprime ou s'enregistre.
 */
export function LienFichePdf({
  besoinId,
  compact,
}: {
  besoinId: string;
  compact?: boolean;
}): JSX.Element {
  return (
    <a
      href={urlFicheBesoin(besoinId)}
      target="_blank"
      rel="noreferrer"
      title="Éditer la fiche de recueil en PDF"
      className={`inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white text-xs font-semibold text-codialis-navy transition hover:bg-gray-50 ${
        compact ? 'min-w-[44px] px-2' : 'px-3'
      }`}
    >
      <FileText className="h-4 w-4 shrink-0" aria-hidden="true" />
      {compact ? <span className="sr-only">Éditer la fiche en PDF</span> : 'Fiche PDF'}
    </a>
  );
}
